import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { EventGroup, EventGroupDefaultValue } from '~/Models/DTO/EventGroup.model';
import { CORE_SERVICE_URLS, EVENT_SERVICE_URLS } from '~/Constants/Urls.constants';
import { ExtendedEventGroup } from './ExtendedEventGroup.model';
import { IEventSeries } from '~/Models/DTO/EventSeries.model';
import { IRequest, IRequestWithCallBack } from '~/Hooks/useRequest';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { ExtendedEventSeries } from './EventCreateSections/EventScheduleSection/extendedEventSeries.model';
import { ExtendedEvent } from './ExtendedEvent.model';
import { EventType, IEvent } from '~/Models/DTO/Event.model';
import { pick } from 'lodash-es';
import { DifferenceFormat } from '~/Models/DTO/Time.model';

export const getEventById = (
  request: IRequest,
  requestWithCallback: IRequestWithCallBack,
  id: string,
  isUSCIntegrationEnabled: boolean,
  isForcedEventGroupUSCSync: boolean,
  setInitialEvent: React.Dispatch<React.SetStateAction<ExtendedEvent>>,
  setEventGroup: (value: React.SetStateAction<ExtendedEventGroup>) => void,
  setSubmissionError: (error: IErrorResponse) => void,
  formatDate: (date: string, pattern: string) => string,
  parse: (dateString: string, formatString: string, referenceDate: number | Date) => Date,
  finishCallback: () => void,
) => {
  requestWithCallback<IEvent>(
    {
      url: routeReplacer(EVENT_SERVICE_URLS.EVENT_DETAIL, { id }),
      method: 'GET',
      actionName: REQUEST_ACTIONS.SINGLE_EVENT,
    },
    async (event) => {
      const cleanedEvent: ExtendedEvent = {
        ...event,
        startDate: event.start,
        endDate: event.end,
        startTime: event.start,
        endTime: event.end,
        sendEmail: false,
      };
      setInitialEvent(cleanedEvent);
      const updateEventGroup = (eventGroup: Partial<ExtendedEventGroup>) => {
        setEventGroup((oldEventGroup) => ({ ...oldEventGroup, ...eventGroup }));
        setInitialEvent((oldValues) => ({
          ...oldValues,
          ...pick(eventGroup, [
            'isUSCIntegrationEnabled',
            'bookingGroupIds',
            'publicCancellationEnabled',
            'publicCancellationRefundEndAsNumber',
            'waitingListEnabled',
            'bookingWindowOpen',
            'publicBookingWindowStartAsNumber',
            'publicBookingWindowEndAsNumber',
            'publicCancellationWindowOpen',
          ]),
        }));
      };
      getEventGroupById(
        request,
        requestWithCallback,
        cleanedEvent.eventGroupId,
        isUSCIntegrationEnabled,
        isForcedEventGroupUSCSync,
        updateEventGroup,
        setSubmissionError,
        formatDate,
        parse,
        event.type,
      );
      finishCallback();
    },
    (error) => {
      setSubmissionError(error);
      finishCallback();
    },
  );
};

const isUsingHourOrMinute = (time: string): number => {
  const TIME_BREAKPOINT = ':';
  try {
    const [, minutes] = time.split(TIME_BREAKPOINT);
    return Number(minutes) ? DifferenceFormat.MINUTES : DifferenceFormat.HOURS;
  } catch {
    return DifferenceFormat.HOURS;
  }
};

const parseWeirdTimeFromTheBackend = (time: string, defaultValue?: number): undefined | number => {
  if (!time) {
    return defaultValue;
  }
  //    input       |      output
  //  -42.02:08:00  |   { days: '42', hours: '02' }
  //    1.12:08:00  |   { days: '1', hours: '12' }
  //      02:08:00  |   { days: undefined, hours: '02' }
  const { days = 0, hours, minutes } = time.match(
    /((?<days>\d{1,5})\.)?((?<hours>\d{1,2}):)?(?<minutes>\d{1,2})/,
  )?.groups ?? {
    days: 0,
    hours: 0,
    minutes: 0,
  };
  const hoursPerDay = 24;
  const minutesPerHour = 60;

  const minutesAsANumber = Number(minutes);

  return isUsingHourOrMinute(time) === DifferenceFormat.MINUTES
    ? Number(hours) * minutesPerHour + minutesAsANumber
    : Number(days) * hoursPerDay + Number(hours);
};

export const getEventGroupById = (
  request: IRequest,
  requestWithCallback: IRequestWithCallBack,
  id: string,
  isUSCIntegrationEnabled: boolean,
  isForcedEventGroupUSCSync: boolean,
  updateEventGroup: (data: Partial<ExtendedEventGroup>) => void,
  setSubmissionError: (error: IErrorResponse) => void,
  formatDate: (date: string, pattern: string) => string,
  parse: (dateString: string, formatString: string, referenceDate: number | Date) => Date,
  eventType: EventType | undefined,
) => {
  const url =
    eventType === EventType.FREE_TRAINING
      ? EVENT_SERVICE_URLS.SINGLE_FREE_TRAININGS
      : EVENT_SERVICE_URLS.SINGLE_EVENTGROUP;

  requestWithCallback<EventGroup>(
    {
      url: routeReplacer(url, { id }),
      method: 'GET',
      actionName: REQUEST_ACTIONS.GET_EVENTGROUP,
    },
    async (eventGroup) => {
      let showInUSCApp = isUSCIntegrationEnabled;
      if (!isForcedEventGroupUSCSync) {
        const syncedEventGroupData = await request<{
          providerId: string;
          eventGroupId: string;
        }>({
          url: routeReplacer(CORE_SERVICE_URLS.SYNCED_EVENT_GROUPS_BY_ID, { id }),
          method: 'GET',
        });
        showInUSCApp = !!syncedEventGroupData.data.eventGroupId;
      }

      const publicCancellationRefundEndAsNumber = parseWeirdTimeFromTheBackend(
        eventGroup.publicCancellationRefundEnd,
        EventGroupDefaultValue.publicCancellationRefundEndAsNumber,
      );

      const extendedEventGroup: ExtendedEventGroup = {
        ...eventGroup,
        isUSCIntegrationEnabled: showInUSCApp,
        timeSeries: [],
        publicCancellationRefundEndAsNumber,
        bookingWindowOpen: eventGroup.publicBookingWindowStart !== null,
        publicBookingWindowStartAsNumber: parseWeirdTimeFromTheBackend(
          eventGroup.publicBookingWindowStart,
        ),
        publicBookingWindowEndAsNumber: parseWeirdTimeFromTheBackend(
          eventGroup.publicBookingWindowEnd,
        ),
        publicCancellationRefundEndType: isUsingHourOrMinute(
          eventGroup.publicCancellationRefundEnd,
        ),
        publicBookingWindowStartType: isUsingHourOrMinute(eventGroup.publicBookingWindowStart),
        publicBookingWindowEndType: isUsingHourOrMinute(eventGroup.publicBookingWindowEnd),
        publicCancellationWindowOpen: !!publicCancellationRefundEndAsNumber,
      };
      updateEventGroup(extendedEventGroup);
      getTimeSeries(
        requestWithCallback,
        id,
        extendedEventGroup.providerId || '',
        updateEventGroup,
        setSubmissionError,
        parse,
      );
    },
    (error) => setSubmissionError(error),
  );
};

const getTimeSeries = (
  requestWithCallback: IRequestWithCallBack,
  eventGroupId: string,
  providerId: string,
  updateEventGroup: (data: Partial<ExtendedEventGroup>) => void,
  setSubmissionError: (error: IErrorResponse) => void,
  parse: (dateString: string, formatString: string, referenceDate: number | Date) => Date,
) => {
  requestWithCallback<IEventSeries[]>(
    {
      url: routeReplacer(EVENT_SERVICE_URLS.EVENT_SERIES_GET, {
        eventGroupId,
        providerId,
      }),
      method: 'GET',
    },
    (eventSeries) => {
      const extendedEventSeries: ExtendedEventSeries[] = eventSeries.map((eventSeries) => ({
        ...eventSeries,
        recurrence: eventSeries.frequency,
        hasEndDate: !!eventSeries.endDate,
        savedOnMobile: true,
        openByDefault: false,
        dailyStartTime: parse(eventSeries.dailyStartTime, 'HH:mm:ss', new Date()).toUTCString(),
        dailyEndTime: parse(eventSeries.dailyEndTime, 'HH:mm:ss', new Date()).toUTCString(),
      }));
      updateEventGroup({ timeSeries: extendedEventSeries });
    },
    (error) => setSubmissionError(error),
  );
};
